// components/FullScreenSupplyChainMap.js
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FaCheckCircle, FaMapMarkerAlt, FaTruck, FaWarehouse, FaPlay, FaPause, FaStepForward, FaStepBackward, FaRedo, FaHome } from "react-icons/fa";
import ReactDOMServer from "react-dom/server";
import axios from 'axios';
import config from '../../envconfig';

// Custom CSS for the exact design from screenshot
const customStyles = `
  .glassmorphism {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }
  
  .gradient-bg {
    background: linear-gradient(135deg, #667eea 0%, #295a80 100%);
  }
  
  .gradient-text {
    background: linear-gradient(135deg, #667eea 0%, #295a80 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  .status-badge {
  //  padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .timeline-item {
    transition: all 0.3s ease;
    border-left: 3px solid #e5e7eb;
    padding-left: 16px;
    margin-left: 8px;
  }
  
  .timeline-item.active {
    border-left-color: #3b82f6;
    background: linear-gradient(90deg, rgba(59, 130, 246, 0.1), transparent);
  }
  
  .timeline-item.completed {
    border-left-color: #10b981;
  }
  
  .timeline-item.pending-inward {
    border-left-color: #fbbf24;
    background: linear-gradient(90deg, rgba(251, 191, 36, 0.05), transparent);
    opacity: 0.7;
  }
  
  .progress-bar {
    background: linear-gradient(90deg, #10b981, #3b82f6, #8b5cf6);
    height: 6px;
    border-radius: 3px;
    transition: width 0.5s ease;
  }
  
  .map-marker {
    background: white;
    border: 3px solid;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .map-marker.current {
    animation: pulse 2s infinite;
    border-color: #3b82f6;
    background: #3b82f6;
  }
  
  .map-marker.completed {
    border-color: #10b981;
    background: #10b981;
  }
  
  .map-marker.pending {
    border-color: #9ca3af;
    background: #9ca3af;
    opacity: 0.6;
  }
  
  @keyframes pulse {
    0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
    70% { transform: scale(1.1); box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); }
    100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
  }
  
  .moving-truck {
    animation: moveTruck 3s infinite;
  }
  
  @keyframes moveTruck {
    0%, 100% { transform: translateX(0) scale(1); }
    50% { transform: translateX(5px) scale(1.05); }
  }
  
  .gradient-btn {
    background: linear-gradient(135deg, #667eea 0%, #295a80 100%);
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.3s ease;
  }
  
  .gradient-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  .gradient-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
  
  .w-400px {
    width: 400px;
    min-width: 400px;
  }
`;

// Custom icons matching the screenshot design
const createCustomIcon = (status, isCurrent = false) => {
  let color, className = 'map-marker';

  if (isCurrent) {
    color = '#3b82f6';
    className += ' current';
  } else if (status === 'completed') {
    color = '#10b981';
    className += ' completed';
  } else {
    color = '#9ca3af';
    className += ' pending';
  }

  return new L.DivIcon({
    html: `<div class="${className}" style="border-color: ${color}; background: ${color};">
             <div style="width: 6px; height: 6px; background: white; border-radius: 50%; margin: 3px;"></div>
           </div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  });
};

// Enhanced moving truck icon with better visibility
const movingTruckIcon = new L.DivIcon({
  html: ReactDOMServer.renderToString(
    <div
      className="moving-truck"
      style={{
        background: "linear-gradient(135deg, #8b5cf6, #a855f7)",
        color: "white",
        borderRadius: "50%",
        width: "40px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 20px rgba(139, 92, 246, 0.6)",
        border: "3px solid white",
        position: "relative",
        zIndex: 1000
      }}
    >
      <FaTruck size={18} />
    </div>
  ),
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  className: 'moving-truck-marker'
});

const FullScreenSupplyChainMap = () => {
  const { rsnNumber } = useParams();
  const navigate = useNavigate();
  const mapRef = useRef();
  
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [animationSpeed, setAnimationSpeed] = useState(2000);
  const [movingPosition, setMovingPosition] = useState(null);
  const [isMovingBetweenPoints, setIsMovingBetweenPoints] = useState(false);

  // Process actual data from API
  const getStatusColor = (status) => {
    const statusLower = String(status || '').toLowerCase().replace(/[^a-z]/g, '');

    if (!statusLower) {
      return 'bg-gray-100 text-gray-800';
    }

    // Remove special characters and normalize
    const normalizedStatus = statusLower.replace(/[^a-z]/g, '');

    if (normalizedStatus.includes('origin') || normalizedStatus.includes('start')) {
      return 'bg-blue-100 text-blue-800';
    }
    if (normalizedStatus.includes('transit')) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (normalizedStatus.includes('completed') || normalizedStatus.includes('delivered') || normalizedStatus.includes('destination')) {
      return 'bg-green-100 text-green-800';
    }
    if (normalizedStatus.includes('current')) {
      return 'bg-purple-100 text-purple-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  // Load data when component mounts
  useEffect(() => {
    const loadRsnLocationData = async () => {
      try {
        setLoading(true);
        
        // Try to get data from sessionStorage first
        const savedData = sessionStorage.getItem('rsnLocationData');
        
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.rsnNumber === rsnNumber) {
            setLocationData(parsedData.data);
            setLoading(false);
            return;
          }
        }

        // If not in sessionStorage, fetch from API
        // You'll need to get company ID from context or sessionStorage
        const companyId = sessionStorage.getItem('UM_CompanyID') || '';
        
        const response = await axios.get(
          `${config.API_URL}/api/rsn-location/${rsnNumber}/${companyId}`
        );

        if (response.data.success) {
          setLocationData(response.data.data);
        } else {
          console.error('No location data found');
        }
      } catch (error) {
        console.error('Error loading RSN location data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (rsnNumber) {
      loadRsnLocationData();
    }
  }, [rsnNumber]);

  // Animation control functions
  const handlePlayPause = () => setIsPlaying(!isPlaying);
  
  const handleReset = () => {
    setCurrentAnimationIndex(0);
    setIsPlaying(false);
  };
  
  const handleNext = () => {
    if (locationData && locationData.locationPath) {
      setCurrentAnimationIndex(prev =>
        prev < locationData.locationPath.length - 1 ? prev + 1 : prev
      );
    }
  };
  
  const handlePrevious = () => {
    setCurrentAnimationIndex(prev => prev > 0 ? prev - 1 : prev);
  };
  
  const handleSpeedChange = (speed) => setAnimationSpeed(speed);
  
  const handleIndexChange = (index) => {
    setCurrentAnimationIndex(index);
    setIsPlaying(false);
  };

  // Enhanced location path processing
  const enhancedLocationPath = locationData?.locationPath?.map((loc, index) => {
    let status = 'pending';
    if (index < currentAnimationIndex) {
      status = 'completed';
    } else if (index === currentAnimationIndex) {
      status = locationData.summary?.journeyStatus === 'COMPLETED' ? 'completed' : 'in-transit';
    }

    // Format timestamp for display with date and time
    const formatDateTime = (timestamp) => {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      return {
        fullDateTime: date.toLocaleString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        date: date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        time: date.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
        day: date.toLocaleDateString('en-IN', { weekday: 'short' })
      };
    };

    const dateTimeInfo = formatDateTime(loc.timestamp);

    let computedStatus = loc.status;
    if (computedStatus === null || computedStatus === undefined || computedStatus === '' || !isNaN(Number(computedStatus))) {
      if (index === 0) {
        computedStatus = 'Origin';
      } else if (index === locationData.locationPath.length - 1) {
        computedStatus = 'Current Location';
      } else {
        computedStatus = 'In-Transit';
      }
    }

    return {
      ...loc,
      name: loc.locationName || `Location ${index + 1}`,
      location: loc.address || `${loc.city || ''} ${loc.state || ''}`.trim() || 'Unknown Location',
      status: computedStatus,
      statusColor: getStatusColor(computedStatus),
      time: dateTimeInfo.time,
      date: dateTimeInfo.date,
      day: dateTimeInfo.day,
      fullDateTime: dateTimeInfo.fullDateTime,
      scpCode: loc.scpCode || 'N/A',
      latitude: loc.latitude,
      longitude: loc.longitude
    };
  }) || [];

  // Check if inward is completed
  const isInwardCompleted = () => {
    if (locationData?.summary?.journeyStatus) {
      const status = locationData.summary.journeyStatus.toLowerCase();
      return status.includes('completed') || status.includes('delivered') || status.includes('destination');
    }
    
    const completedLocations = enhancedLocationPath.filter(loc => 
      loc.status && loc.status.toLowerCase().includes('completed')
    );
    return completedLocations.length > 0;
  };

  // Get coordinates for animation - only show if inward is completed
  const animatedRouteCoordinates = isInwardCompleted() 
    ? enhancedLocationPath
        .slice(0, currentAnimationIndex + 1)
        .filter(loc => loc.latitude && loc.longitude)
        .map(loc => [loc.latitude, loc.longitude])
    : [];

  const fullRouteCoordinates = isInwardCompleted()
    ? enhancedLocationPath
        .filter(loc => loc.latitude && loc.longitude)
        .map(loc => [loc.latitude, loc.longitude])
    : [];

  // Calculate intermediate positions for smooth movement
  const calculateIntermediatePositions = (startIndex, endIndex, steps = 50) => {
    if (startIndex >= endIndex) return [];

    const start = enhancedLocationPath[startIndex];
    const end = enhancedLocationPath[endIndex];

    if (!start || !end || !start.latitude || !start.longitude || !end.latitude || !end.longitude) {
      return [];
    }

    const positions = [];
    for (let i = 0; i <= steps; i++) {
      const ratio = i / steps;
      const lat = start.latitude + (end.latitude - start.latitude) * ratio;
      const lng = start.longitude + (end.longitude - start.longitude) * ratio;
      positions.push([lat, lng]);
    }
    return positions;
  };

  // Animate RSN movement - only if inward is completed
  useEffect(() => {
    if (!isInwardCompleted()) {
      setIsMovingBetweenPoints(false);
      setMovingPosition(null);
      return;
    }

    if (!isPlaying || currentAnimationIndex >= enhancedLocationPath.length - 1) {
      setIsMovingBetweenPoints(false);
      setMovingPosition(null);
      return;
    }

    const startIndex = currentAnimationIndex;
    const endIndex = currentAnimationIndex + 1;
    const intermediatePositions = calculateIntermediatePositions(startIndex, endIndex, 30);

    if (intermediatePositions.length === 0) {
      setIsMovingBetweenPoints(false);
      setMovingPosition(null);
      return;
    }

    setIsMovingBetweenPoints(true);
    let currentStep = 0;

    const moveInterval = setInterval(() => {
      if (currentStep < intermediatePositions.length) {
        setMovingPosition(intermediatePositions[currentStep]);
        currentStep++;
      } else {
        clearInterval(moveInterval);
        setIsMovingBetweenPoints(false);
        setMovingPosition(null);
        if (currentAnimationIndex < enhancedLocationPath.length - 1) {
          handleNext();
        }
      }
    }, animationSpeed / intermediatePositions.length);

    return () => clearInterval(moveInterval);
  }, [isPlaying, currentAnimationIndex, enhancedLocationPath.length, animationSpeed]);

  // Auto-center map
  useEffect(() => {
    if (mapRef.current && enhancedLocationPath.length > 0) {
      const map = mapRef.current;
      
      const outwardLocation = enhancedLocationPath[0];
      
      if (!isInwardCompleted() && outwardLocation) {
        if (outwardLocation.latitude && outwardLocation.longitude) {
          map.setView([outwardLocation.latitude, outwardLocation.longitude], 7, {
            animate: true,
            duration: 1
          });
        }
      } else {
        const currentLocation = enhancedLocationPath[currentAnimationIndex];
        const center = movingPosition
          ? movingPosition
          : currentLocation
          ? [currentLocation.latitude, currentLocation.longitude]
          : [22.2587, 71.1924];

        map.setView(center, 7, {
          animate: true,
          duration: 1
        });
      }
    }
  }, [currentAnimationIndex, movingPosition, isInwardCompleted, enhancedLocationPath]);

  // Inject custom styles
  useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);

    return () => {
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, []);

  // Early return if loading
  if (loading) {
    return (
      <div className="min-vh-100 bg-gradient-to-br from-blue-50 to-purple-50 d-flex align-items-center justify-content-center p-4">
        <div className="glassmorphism rounded-2xl p-8 text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Loading Supply Chain Journey</h3>
          <p className="text-gray-600">Loading data for RSN: {rsnNumber}</p>
        </div>
      </div>
    );
  }

  // Early return if no data
  if (!locationData || !locationData.locationPath || locationData.locationPath.length === 0) {
    return (
      <div className="min-vh-100 bg-gradient-to-br from-blue-50 to-purple-50 d-flex align-items-center justify-content-center p-4">
        <div className="glassmorphism rounded-2xl p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full d-flex align-items-center justify-content-center mx-auto mb-4">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">No Journey Data Available</h3>
          <p className="text-gray-600 mb-4">No location data available for RSN: {rsnNumber}</p>
          {/* <button 
            className="gradient-btn"
            onClick={() => navigate(-1)}
          >
            <FaHome className="me-2" />
            Back to SCT
          </button> */}
        </div>
      </div>
    );
  }

  const progressPercentage = ((currentAnimationIndex + 1) / enhancedLocationPath.length) * 100;
  const currentStep = enhancedLocationPath[currentAnimationIndex];
  const outwardLocation = enhancedLocationPath.length > 0 ? enhancedLocationPath[0] : null;

  return (
    <div className="vh-100 d-flex flex-column bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <div className="bg-white text-white p-3 d-flex justify-content-between align-items-center">
        <div>
          <h2 className="text-md font-bold gradient-text">Supply Chain Journey : {rsnNumber}</h2>
          {/* <small className="opacity-75">Tracking RSN movement across locations</small> */}
        </div>
        {/* <button 
          className="btn gradient-btn d-flex align-items-center gap-2"
          onClick={() => navigate(-1)}
        >
          <FaHome />
          Back to SCT
        </button> */}
      </div>

      {/* Main Content */}
      <div className="flex-grow-1 p-3 d-flex gap-3 overflow-hidden">
        {/* Map Container - 70% width */}
        <div className="flex-grow-1 glassmorphism rounded-2xl overflow-hidden">
          <MapContainer
            ref={mapRef}
            center={!isInwardCompleted() && outwardLocation ? [outwardLocation.latitude, outwardLocation.longitude] : 
                   currentStep ? [currentStep.latitude, currentStep.longitude] : [22.2587, 71.1924]}
            zoom={7}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />

            {/* Show route only if inward is completed */}
            {isInwardCompleted() && animatedRouteCoordinates.length > 0 && (
              <Polyline
                positions={[
                  ...animatedRouteCoordinates,
                  ...(movingPosition ? [movingPosition] : [])
                ]}
                color="#3b82f6"
                weight={4}
                opacity={0.9}
              />
            )}

            {isInwardCompleted() && fullRouteCoordinates.length > 1 && animatedRouteCoordinates.length < fullRouteCoordinates.length && (
              <Polyline
                positions={fullRouteCoordinates.slice(animatedRouteCoordinates.length - 1)}
                color="#e5e7eb"
                weight={2}
                opacity={0.5}
                dashArray="5, 10"
              />
            )}

            {/* Moving RSN Marker - only show if inward is completed */}
            {isInwardCompleted() && movingPosition && (
              <Marker position={movingPosition} icon={movingTruckIcon}>
                <Popup>
                  <div className="p-3" style={{ minWidth: '200px' }}>
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-circle p-2">
                        <FaTruck className="text-white" />
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold">RSN in Transit</h6>
                        <small className="text-muted">{locationData.rsnInfo?.rsnNumber}</small>
                      </div>
                    </div>
                    <div className="small">
                      <div><strong>From:</strong> {enhancedLocationPath[currentAnimationIndex]?.name}</div>
                      <div><strong>To:</strong> {enhancedLocationPath[currentAnimationIndex + 1]?.name}</div>
                      <div><strong>Status:</strong> <span className="text-warning">In Transit</span></div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Show truck at outward location if inward not completed */}
            {!isInwardCompleted() && outwardLocation && (
              <Marker
                position={[outwardLocation.latitude, outwardLocation.longitude]}
                icon={movingTruckIcon}
              >
                <Popup>
                  <div className="p-3" style={{ minWidth: '200px' }}>
                    <div className="d-flex align-items-center gap-3 mb-2">
                      <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-circle p-2">
                        <FaTruck className="text-white" />
                      </div>
                      <div>
                        <h6 className="mb-0 fw-bold">RSN at Outward Location</h6>
                        <small className="text-muted">{locationData.rsnInfo?.rsnNumber}</small>
                      </div>
                    </div>
                    <div className="small">
                      <div><strong>Location:</strong> {outwardLocation.name}</div>
                      <div><strong>Status:</strong> <span className="text-warning">Inward Not Completed</span></div>
                      <div><strong>Address:</strong> {outwardLocation.location}</div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Location Markers - only show if inward is completed */}
            {isInwardCompleted() && enhancedLocationPath.map((location, index) => {
              const isCurrent = index === currentAnimationIndex && !isMovingBetweenPoints;
              const isCompleted = index < currentAnimationIndex;

              if (isMovingBetweenPoints && index === currentAnimationIndex) return null;

              return (
                <Marker
                  key={index}
                  position={[location.latitude, location.longitude]}
                  icon={createCustomIcon(isCompleted ? 'completed' : 'pending', isCurrent)}
                >
                  <Popup>
                    <div className="p-3" style={{ minWidth: '200px' }}>
                      <h6 className="fw-bold mb-2">{location.name}</h6>
                      <div className="small text-muted mb-2">{location.location}</div>
                      <div className="d-flex flex-wrap gap-1">
                        <span className={`status-badge ${getStatusColor(location.status)}`}>
                          {location.status}
                        </span>
                      </div>
                      <div className="small mt-2">
                        <div><strong>Date:</strong> {location.date}</div>
                        <div><strong>Time:</strong> {location.time}</div>
                        <div><strong>SCP Code:</strong> {location.scpCode}</div>
                      </div>
                    </div>
                  </Popup>
                  <Tooltip permanent>
                    {location.name} ({location.status})
                  </Tooltip>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        {/* Side Panel - 30% width */}
        <div className="w-400px d-flex flex-column gap-3">
          {/* Animation Controls */}
          <div className="glassmorphism rounded-2xl p-3">
            <div className="d-flex align-items-center gap-2 justify-content-between">
              <button
                onClick={handleReset}
                disabled={currentAnimationIndex === 0}
                className="gradient-btn reset-btn d-flex align-items-center gap-2"
              >
                <FaRedo />
                <span>Reset</span>
              </button>

              <button
                onClick={handlePrevious}
                disabled={currentAnimationIndex === 0}
                className="gradient-btn prev-btn d-flex align-items-center gap-2"
              >
                <FaStepBackward />
                <span>Previous</span>
              </button>

              <button
                onClick={handlePlayPause}
                className="gradient-btn play-btn d-flex align-items-center gap-2 px-3"
              >
                {isPlaying ? (
                  <>
                    <FaPause />
                    <span>Pause</span>
                  </>
                ) : (
                  <>
                    <FaPlay />
                    <span>Play</span>
                  </>
                )}
              </button>

              <button
                onClick={handleNext}
                disabled={currentAnimationIndex >= enhancedLocationPath.length - 1}
                className="gradient-btn next-btn d-flex align-items-center gap-2"
              >
                <span>Next</span>
                <FaStepForward />
              </button>
            </div>
          </div>

          {/* Current Status Card */}
          <div className="glassmorphism rounded-2xl p-3 flex-grow-0">
            <div className="d-flex align-items-center gap-3 mb-2">
              <div className="bg-primary text-white rounded-circle p-2">
                <FaMapMarkerAlt className="text-white" />
              </div>
              <div>
                <h5 className="fw-bold mb-0 text-primary">
                  {!isInwardCompleted() ? 'Outward Location' : currentStep?.name || 'Current Location'}
                </h5>
                <span className="text-muted">
                  {!isInwardCompleted() ? outwardLocation?.location || 'Unknown Location' : currentStep?.location || 'Unknown Location'}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Status</span>
                <span className={`status-badge ${!isInwardCompleted() ? 'bg-yellow-100 text-yellow-800' : getStatusColor(currentStep?.status)}`}>
                  {!isInwardCompleted() ? 'Inward Pending' : currentStep?.status || 'Unknown'}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Date</span>
                <span className="fw-semibold">
                  {!isInwardCompleted() ? outwardLocation?.date || 'N/A' : currentStep?.date || 'N/A'}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">Time</span>
                <span className="fw-semibold">
                  {!isInwardCompleted() ? outwardLocation?.time || 'N/A' : currentStep?.time || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Journey Timeline */}
          <div className="glassmorphism rounded-2xl p-3 flex-grow-1 overflow-auto">
            <h6 className="fw-bold mb-3">Journey Timeline</h6>
            <div className="space-y-2">
              {enhancedLocationPath.map((step, index) => {
                const isActive = index === currentAnimationIndex;
                const isCompleted = index < currentAnimationIndex;
                const statusLower = String(step.status || '').toLowerCase();

                const timelineItemClass = !isInwardCompleted() 
                  ? 'pending-inward' 
                  : isActive 
                    ? 'active' 
                    : isCompleted 
                      ? 'completed' 
                      : '';

                return (
                  <div
                    key={index}
                    className={`timeline-item ${timelineItemClass} p-2 rounded`}
                    style={{ 
                      cursor: 'pointer',
                      ...(!isInwardCompleted() && {
                        opacity: 0.6,
                        filter: 'grayscale(0.3)'
                      })
                    }}
                    onClick={() => handleIndexChange(index)}
                  >
                    <div className="d-flex align-items-start gap-2">
                      <div className={`rounded-circle p-2 ${
                        !isInwardCompleted() 
                          ? 'bg-gray-300 text-gray-600' 
                          : isActive 
                            ? 'bg-primary text-white' 
                            : isCompleted 
                              ? 'bg-success text-white' 
                              : 'bg-light text-muted'
                      }`}>
                        {statusLower.includes('transit') ? <FaTruck size={12} /> :
                          statusLower.includes('origin') ? <FaWarehouse size={12} /> :
                            statusLower.includes('destination') ? <FaCheckCircle size={12} /> :
                              <FaMapMarkerAlt size={12} />}
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold small">{step.name}</div>
                        <div className="small text-muted mb-1">{step.location}</div>
                        <div className="small text-primary fw-semibold">{step.day}, {step.date}</div>
                        <div className="small text-muted">{step.time}</div>
                      </div>
                      <span className={`status-badge small ${
                        !isInwardCompleted() 
                          ? 'bg-gray-200 text-gray-600' 
                          : getStatusColor(step.status)
                      }`}>
                        {!isInwardCompleted() && index > 0 ? 'Pending' : step.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullScreenSupplyChainMap;